import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import {
  approveDeferredTrainerEbay, fetchDeferredTrainerEbayQueue,
  fetchDeferredTrainerEbayStatus, previewDeferredTrainerEbay,
} from "./trainerInventoryApi";
import { actionLabel, canApproveDeferredEbay, deferredEbayRoleRows } from "./deferredTrainerEbayUtils";

const errorText = (err) => err?.response?.data?.error || err?.message || "Request failed";
const formatNumber = (value) => value == null ? "Unknown" : Number(value).toLocaleString("en-AU");

export default function DeferredTrainerEbayReview() {
  const [events, setEvents] = useState([]);
  const [afterId, setAfterId] = useState(0);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const inFlightRef = useRef(false);

  const loadQueue = useCallback(async (pageAfter = 0) => {
    if (inFlightRef.current) return;
    setLoading(true); setError("");
    try {
      const result = await fetchDeferredTrainerEbayQueue(pageAfter);
      const data = result.data || {};
      setEvents((old) => pageAfter ? [...old, ...(data.events || [])] : data.events || []);
      setAfterId(data.next_after_id || pageAfter);
      setMore(Boolean(data.has_more));
    } catch (err) { setError(errorText(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const selectPreview = async (item) => {
    if (busy) return;
    setSelected(item); setPreview(null); setConfirmed(false);
    setDialogOpen(false); setError(""); setMessage(""); setBusy(true);
    try {
      const response = await previewDeferredTrainerEbay(item.event_id, item.role);
      setPreview(response.data || null);
      if (response.data?.status === "manual_review")
        setMessage(response.data.message || "Manual investigation required; no automatic approval is available.");
    } catch (err) { setError(errorText(err)); }
    finally { setBusy(false); }
  };

  const checkStatus = async (item) => {
    if (busy) return;
    setBusy(true); setError(""); setMessage("");
    try {
      const response = await fetchDeferredTrainerEbayStatus(item.event_id, item.role);
      const status = response.data || {};
      setSelected(item); setPreview(null); setConfirmed(false); setDialogOpen(false);
      setMessage(`Event #${item.event_id}, ${item.role}: ${status.reconciliation_status}. ${status.last_error || "No outstanding error recorded."}`);
      await loadQueue();
    } catch (err) { setError(errorText(err)); }
    finally { setBusy(false); }
  };

  const submitApproval = async () => {
    if (inFlightRef.current || !selected || !canApproveDeferredEbay(preview, confirmed, busy)) return;
    inFlightRef.current = true;
    setDialogOpen(false); setBusy(true); setError(""); setMessage("");
    const attempted = { ...selected };
    try {
      const response = await approveDeferredTrainerEbay(selected.event_id, selected.role, preview.preview_token);
      if (response.data?.status !== "applied") throw new Error("Approval outcome not verified");
      setMessage(response.data.action === "create_draft"
        ? "Local draft created and linked. Nothing was published to eBay."
        : `eBay ${response.data.action} verified. Applied event #${selected.event_id} once.`);
      setPreview(null); setConfirmed(false);
      await loadQueue();
    } catch (err) {
      setPreview(null); setConfirmed(false);
      setError(`Approval outcome may be uncertain: ${errorText(err)}. Do NOT repeat approval. Use Check status and inspect eBay before further actions.`);
      // Intentionally do NOT retry: even 502/network failures can follow a successful remote write.
      setSelected(attempted);
    } finally { inFlightRef.current = false; setBusy(false); }
  };

  return <Stack spacing={2} sx={{ pb: 4 }}>
    <Box><Typography variant="h5">Deferred Allocation → eBay Review</Typography>
      <Typography variant="body2" color="text.secondary">
        Review each deferred allocation event independently. Refreshing a preview does not change eBay quantities.
        Approving a replenishment or relist DOES change eBay; creating a draft is local-only. Never retry an uncertain approval.
      </Typography>
    </Box>
    <Stack direction="row" gap={1} alignItems="center">
      <Button disabled={loading || busy} variant="outlined" onClick={() => { setSelected(null); setPreview(null); loadQueue(); }}>Reload queue</Button>
      <Chip label={`${deferredEbayRoleRows(events).length} role allocations loaded`} />
      {(busy || loading) && <CircularProgress size={20} aria-label="Loading reconciliation" />}
    </Stack>
    {error && <Alert severity="error" role="alert">{error}</Alert>}
    {message && <Alert severity="info" role="status">{message}</Alert>}
    <Stack spacing={1.5} sx={{ display: { xs: "flex", md: "none" } }} aria-label="Mobile deferred allocation reconciliation queue">
      {deferredEbayRoleRows(events).map((item) => <Card variant="outlined" key={`${item.event_id}-${item.role}`}>
        <CardContent><Stack spacing={1.5}>
          <Typography fontWeight={700} sx={{ overflowWrap: "anywhere" }}>{item.card_name} — {item.card_variant}</Typography>
          <Typography variant="body2" color="text.secondary">Allocation #{item.event_id} · Receipt #{item.intake_row_id}</Typography>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Chip label={item.role === "playset" ? "Playset" : "Single"} size="small" />
            <Typography variant="body2">New units: {formatNumber(item.quantity)}</Typography>
            <Chip size="small" label={item.status} color={item.status === "applied" ? "success" : ["applying", "uncertain"].includes(item.status) ? "warning" : "default"} />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button fullWidth size="small" variant="outlined" disabled={busy || ["applied", "applying", "uncertain"].includes(item.status)} onClick={() => selectPreview(item)}>Refresh and preview</Button>
            <Button fullWidth size="small" variant="outlined" disabled={busy} onClick={() => checkStatus(item)}>Check status</Button>
          </Stack>
        </Stack></CardContent>
      </Card>)}
      {!events.length && <Alert severity="info">No deferred allocations found.</Alert>}
    </Stack>
    <TableContainer component={Card} variant="outlined" sx={{ display: { xs: "none", md: "block" } }}><Table size="small" aria-label="Deferred allocation reconciliation queue">
      <TableHead><TableRow>
        <TableCell>Card / receipt</TableCell><TableCell>Role</TableCell><TableCell align="right">New units</TableCell>
        <TableCell>Status</TableCell><TableCell>Actions</TableCell>
      </TableRow></TableHead>
      <TableBody>{deferredEbayRoleRows(events).map((item) =>
        <TableRow key={`${item.event_id}-${item.role}`} selected={selected?.event_id === item.event_id && selected?.role === item.role}>
          <TableCell><Typography fontWeight={600}>{item.card_name} — {item.card_variant}</Typography>
            <Typography variant="caption">Allocation #{item.event_id}, receipt #{item.intake_row_id}</Typography></TableCell>
          <TableCell>{item.role}</TableCell><TableCell align="right">{formatNumber(item.quantity)}</TableCell>
          <TableCell><Chip size="small" color={item.status === "applied" ? "success" : ["applying", "uncertain"].includes(item.status) ? "warning" : "default"} label={item.status} /></TableCell>
          <TableCell><Stack direction="row" spacing={1}>
            <Button size="small" disabled={busy || ["applied", "applying", "uncertain"].includes(item.status)} onClick={() => selectPreview(item)}>Refresh and preview</Button>
            <Button size="small" disabled={busy} onClick={() => checkStatus(item)}>Check status</Button>
          </Stack></TableCell>
        </TableRow>)}
        {!events.length && <TableRow><TableCell colSpan={5}>No deferred allocations found.</TableCell></TableRow>}
      </TableBody>
    </Table></TableContainer>
    {more && <Button variant="outlined" disabled={busy || loading} onClick={() => loadQueue(afterId)}>Load more</Button>}
    {preview && selected && <Card variant="outlined" sx={{ borderColor: "primary.main" }}><CardContent><Stack spacing={1.5}>
      <Typography variant="h6">Review allocation #{selected.event_id} — {selected.role}</Typography>
      <Typography fontWeight={600}>{actionLabel(preview.action)}</Typography>
      <Typography>New units: {formatNumber(preview.allocation_quantity)} · Listing #{preview.listing_id || "Unlinked"}</Typography>
      <Typography>Verified current quantity: {formatNumber(preview.baseline_quantity)} · Proposed quantity: {formatNumber(preview.proposed_quantity)}</Typography>
      <Typography variant="caption" color="text.secondary">Quantity source: {preview.quantity_source || "Not trusted"} · Preview valid for up to 10 minutes, unless eBay or linkage changes.</Typography>
      {preview.message && <Alert severity={preview.status === "manual_review" ? "warning" : "info"}>{preview.message}</Alert>}
      {preview.status === "ready_for_approval" ? <>
        <FormControlLabel control={<Checkbox checked={confirmed} onChange={(_, checked) => setConfirmed(checked)} />} label={preview.action === "create_draft"
          ? "I approve creating this local draft; no eBay listing will be published."
          : "I checked the live eBay quantity and approve this exact listing action."} />
        <Stack direction="row" gap={1}>
          <Button variant="contained" color={preview.action === "create_draft" ? "primary" : "warning"}
            disabled={!canApproveDeferredEbay(preview, confirmed, busy)} onClick={() => setDialogOpen(true)}>Continue to approval</Button>
          <Button variant="outlined" onClick={() => { setPreview(null); setConfirmed(false); }} disabled={busy}>Cancel preview</Button>
        </Stack>
      </> : <Alert severity="warning">Manual review required. No approval action is available.</Alert>}
    </Stack></CardContent></Card>}
    <Dialog open={dialogOpen && Boolean(preview)} onClose={() => { if (!busy) setDialogOpen(false); }} aria-labelledby="deferred-ebay-confirm-title" fullWidth maxWidth="sm">
      <DialogTitle id="deferred-ebay-confirm-title">Confirm {actionLabel(preview?.action)}</DialogTitle>
      <DialogContent><Typography>
        Allocation #{selected?.event_id} · {selected?.role} · {formatNumber(preview?.allocation_quantity)} new units.
        {preview?.action !== "create_draft" ? ` This will send eBay quantity ${formatNumber(preview?.proposed_quantity)} after a final live verification.` : " This creates an internal draft, not a live eBay listing."}
      </Typography><Alert severity="warning" sx={{ mt: 2 }}>This approval cannot be automatically repeated. An uncertain response requires manual review.</Alert></DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setDialogOpen(false)}>Cancel</Button>
        <Button disabled={!canApproveDeferredEbay(preview, confirmed, busy)} variant="contained" color="warning" onClick={submitApproval}>Confirm explicit approval</Button></DialogActions>
    </Dialog>
  </Stack>;
}
